import { lazy, Suspense } from "react";
import Head from "@/lib/head";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster as HotToaster } from "react-hot-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { SiteSchema, WebSiteSchema } from "@/lib/schema";
import "./i18n";
import Index from "./pages/Index";

const About = lazy(() => import("./pages/About"));
const TrainingCourses = lazy(() => import("./pages/TrainingCourses"));
const Sectors = lazy(() => import("./pages/Sectors"));
const Cities = lazy(() => import("./pages/Cities"));
const Contact = lazy(() => import("./pages/Contact"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OurStory = lazy(() => import("./pages/OurStory"));
const MeetOurTeam = lazy(() => import("./pages/MeetOurTeam"));
const CoursePage = lazy(() => import("./pages/course"));
const PrivacyPolicy = lazy(() => import("./pages/Privacy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DigitalSolutions = lazy(() => import("./pages/DigitalSolutions"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const baseUrl = "https://ecosafe.com.sa";

const seoConfig: Record<
  string,
  {
    title?: string;
    description?: string;
    image?: string;
    type?: "website" | "article" | "course";
    structuredData?: object | object[];
    noIndex?: boolean;
    noFollow?: boolean;
  }
> = {
  "/": {
    title: "Home",
    description:
      "ECOSAFE provides professional training and consulting services across Saudi Arabia and beyond. Transform your organization and individuals through development excellence.",
    type: "website",
    structuredData: [SiteSchema, WebSiteSchema],
  },
  "/about": {
    title: "About Us",
    description:
      "Learn about ECOSAFE's mission, vision, and values. Leading professional training and consulting across Saudi Arabia and beyond.",
    type: "website",
  },
  "/about/our-story": {
    title: "Our Story",
    description:
      "Discover ECOSAFE's journey from 2019 to global impact - 250+ programs, 60+ companies, 25+ countries, 3000+ professionals empowered.",
    type: "article",
  },
  "/about/meet-our-team": {
    title: "Meet Our Team",
    description:
      "Meet ECOSAFE's leadership team and expert trainers with 200+ years combined experience in training, consulting, and leadership development.",
    type: "website",
  },
  "/training-courses": {
    title: "Training Courses",
    description:
      "Explore 250+ professional training courses across Saudi Arabia, UAE, Egypt, USA, and Sweden. Categories: HSE, Leadership, Project Management, Quality, Supply Chain.",
    type: "website",
  },
  "/courses": {
    title: "Training Courses",
    description:
      "Explore 250+ professional training courses across Saudi Arabia, UAE, Egypt, USA, and Sweden. Categories: HSE, Leadership, Project Management, Quality, Supply Chain.",
    type: "website",
  },
  "/sectors": {
    title: "Training Categories",
    description:
      "Browse professional training categories and sectors. Filter by popularity, level, and topic. Find courses in HSE, Management, Engineering, Quality, and more.",
    type: "website",
  },
  "/digital-solutions": {
    title: "Digital Transformation & Technology Solutions",
    description:
      "ECOSAFE digital solutions: competency and workforce management, AI-powered services, document and records management, workflow automation, migration advisory, and business intelligence.",
    type: "website",
  },
  "/cities": {
    title: "Training Cities",
    description:
      "Find training courses in cities across Saudi Arabia (Riyadh, Jeddah, Dammam), UAE (Dubai), Egypt (Cairo), USA (Iowa), Sweden (Stockholm).",
    type: "website",
  },
  "/contact": {
    title: "Contact Us",
    description:
      "Get in touch with ECOSAFE for training and consulting services. Head Office: Riyadh, Saudi Arabia. Phone: +966 11 506 2744. Email: info@ecosafe.com.sa",
    type: "website",
  },
  "/search": {
    title: "Search Results",
    description:
      "Search for training courses, categories, instructors, and topics.",
    type: "website",
    noIndex: true,
    noFollow: true,
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description:
      "ECOSAFE Privacy Policy - Data protection under Saudi Personal Data Protection Law (PDPL). Learn how we collect, use, and protect your data.",
    type: "website",
  },
  "/terms-of-service": {
    title: "Terms of Service",
    description:
      "ECOSAFE Terms of Service and Conditions of Use. Governs your use of our website and training services.",
    type: "website",
  },
};

const SEOHead = () => {
  const location = useLocation();
  const path = location.pathname;

  const isCoursePage = path.startsWith("/course/");

  const config = isCoursePage
    ? {}
    : seoConfig[path] || { title: "Page Not Found", noIndex: true };

  // /courses duplicates /training-courses, so point search engines at one URL
  const canonicalPath = path === "/courses" ? "/training-courses" : path;
  const fullUrl = `${baseUrl}${canonicalPath}`;

  return (
    <Head
      title={config.title}
      description={config.description}
      image={config.image}
      url={fullUrl}
      type={config.type}
      structuredData={config.structuredData}
      noIndex={config.noIndex}
      noFollow={config.noFollow}
    />
  );
};

const AppContent = () => {
  const { isRTL } = useLanguage();

  return (
    <>
      <SEOHead />
      <div className={`min-h-screen ${isRTL ? "rtl" : "ltr"}`}>
        <HotToaster
          position={isRTL ? "top-left" : "top-right"}
          toastOptions={{
            duration: 4000,
            style: {
              background: "hsl(var(--card))",
              color: "hsl(var(--card-foreground))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              fontSize: "14px",
            },
            success: {
              iconTheme: {
                primary: "hsl(var(--primary))",
                secondary: "hsl(var(--primary-foreground))",
              },
            },
            error: {
              iconTheme: {
                primary: "hsl(var(--destructive))",
                secondary: "hsl(var(--destructive-foreground))",
              },
            },
          }}
        />
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/our-story" element={<OurStory />} />
            <Route path="/about/meet-our-team" element={<MeetOurTeam />} />
            <Route path="/training-courses" element={<TrainingCourses />} />
            <Route path="/courses" element={<TrainingCourses />} />
            <Route path="/sectors" element={<Sectors />} />
            <Route path="/digital-solutions" element={<DigitalSolutions />} />
            <Route path="/cities" element={<Cities />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/course/:id" element={<CoursePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
